import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft, UserPlus, RefreshCw, Mail, Clock, CheckCircle, AlertCircle,
  Shield, Users, Send, Copy, Loader2, UserCog
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const ADMIN_ROLES = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full access to all features' },
  { value: 'finance_admin', label: 'Finance Admin', description: 'Financial data, payouts, SSN access' },
  { value: 'client_admin', label: 'Client Admin', description: 'Client management, bookings, contracts' },
  { value: 'talent_admin', label: 'Talent Admin', description: 'Talent management, onboarding, CSV import' }
];

const AdminManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', admin_role: '', name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [resendingFor, setResendingFor] = useState(null);

  const fetchPendingInvites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/pending-admin-invites');
      setPendingInvites(res.data);
    } catch (error) {
      console.error('Failed to fetch pending invites:', error);
      if (error.response?.status === 403) {
        toast.error('Only Super Admin can access this page');
        navigate('/admin/home');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPendingInvites();
  }, [fetchPendingInvites]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.admin_role) {
      toast.error('Email and role are required');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/admin/invite-admin', inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      
      // Copy link to clipboard
      if (res.data.invite_link) {
        await navigator.clipboard.writeText(res.data.invite_link);
        toast.info('Invite link copied to clipboard');
      }
      
      setInviteDialog(false);
      setInviteForm({ email: '', admin_role: '', name: '' });
      fetchPendingInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (email) => {
    setResendingFor(email);
    try {
      const res = await api.post('/admin/resend-admin-invite', { email });
      toast.success(`Invite resent to ${email}`);
      
      // Copy new link to clipboard
      if (res.data.invite_link) {
        await navigator.clipboard.writeText(res.data.invite_link);
        toast.info('New invite link copied to clipboard');
      }
      
      fetchPendingInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingFor(null);
    }
  };

  const copyLink = async (email) => {
    // We need to resend to get a fresh link since tokens may have changed
    toast.info('Generating new link...');
    await handleResend(email);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden" data-testid="admin-management">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} className="shrink-0">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
                  <UserCog className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500 shrink-0" />
                  <span className="truncate">Admin Management</span>
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground">Invite and manage admin users</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
              <Button onClick={fetchPendingInvites} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              <Button onClick={() => setInviteDialog(true)} size="sm" className="bg-purple-600 hover:bg-purple-700">
                <UserPlus className="w-4 h-4 mr-1" />
                Invite Admin
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Setup</p>
                <p className="text-2xl font-bold">{pendingInvites.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Super Admins</p>
                <p className="text-2xl font-bold">
                  {pendingInvites.filter(i => i.admin_role === 'super_admin').length}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expired Links</p>
                <p className="text-2xl font-bold">
                  {pendingInvites.filter(i => i.is_expired).length}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Role Types</p>
                <p className="text-2xl font-bold">4</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Invites */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle>Pending Admin Invites</CardTitle>
            <CardDescription>
              Admins who haven't completed their account setup yet
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingInvites.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                <p className="font-medium text-lg">All admins have set up their accounts</p>
                <p className="text-muted-foreground">Invite new admins using the button above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingInvites.map((invite) => (
                  <div 
                    key={invite.user_id} 
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border ${
                      invite.is_expired ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        invite.admin_role === 'super_admin' ? 'bg-purple-100' :
                        invite.admin_role === 'finance_admin' ? 'bg-green-100' :
                        invite.admin_role === 'client_admin' ? 'bg-blue-100' :
                        'bg-amber-100'
                      }`}>
                        <Mail className={`w-5 h-5 ${
                          invite.admin_role === 'super_admin' ? 'text-purple-600' :
                          invite.admin_role === 'finance_admin' ? 'text-green-600' :
                          invite.admin_role === 'client_admin' ? 'text-blue-600' :
                          'text-amber-600'
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium break-all text-sm">{invite.email}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className={`text-xs ${
                            invite.admin_role === 'super_admin' ? 'border-purple-300 text-purple-700' :
                            invite.admin_role === 'finance_admin' ? 'border-green-300 text-green-700' :
                            invite.admin_role === 'client_admin' ? 'border-blue-300 text-blue-700' :
                            'border-amber-300 text-amber-700'
                          }`}>
                            {invite.role_display}
                          </Badge>
                          {invite.is_expired ? (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Link Expired
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Expires: {formatDateMonthDay(invite.expires_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyLink(invite.email)}
                        disabled={resendingFor === invite.email}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Link
                      </Button>
                      <Button 
                        variant={invite.is_expired ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleResend(invite.email)}
                        disabled={resendingFor === invite.email}
                        className={invite.is_expired ? 'bg-red-600 hover:bg-red-700' : ''}
                      >
                        {resendingFor === invite.email ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Send className="w-4 h-4 mr-1" />
                        )}
                        {invite.is_expired ? 'Send New Link' : 'Resend'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Roles Reference */}
        <Card className="bg-white mt-6">
          <CardHeader>
            <CardTitle>Admin Roles Reference</CardTitle>
            <CardDescription>Permission levels for each admin role</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ADMIN_ROLES.map((role) => (
                <div key={role.value} className="p-4 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className={`w-5 h-5 ${
                      role.value === 'super_admin' ? 'text-purple-600' :
                      role.value === 'finance_admin' ? 'text-green-600' :
                      role.value === 'client_admin' ? 'text-blue-600' :
                      'text-amber-600'
                    }`} />
                    <span className="font-medium">{role.label}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{role.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Invite New Admin
            </DialogTitle>
            <DialogDescription>
              Send an invitation email with a secure link to set up their account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name (Optional)</Label>
              <Input
                id="name"
                placeholder="Admin's name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Admin Role *</Label>
              <Select
                value={inviteForm.admin_role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, admin_role: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col">
                        <span>{role.label}</span>
                        <span className="text-xs text-muted-foreground">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={submitting}>
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Invitation</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
